// src/pages/Contact.jsx
import { motion } from "framer-motion";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaChevronRight,
  FaCalendarAlt,
} from "react-icons/fa";
import { useState, useEffect } from "react";
import "leaflet/dist/leaflet.css";
import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import { contactApi } from "../services/contactApi";
import translations from "../contexts/translations";
import LeafletMap from "../components/LeafletMap";

// ✅ Ajoute le pattern comme sur Services
import zelijPattern from "../assets/zelij3.png";

const Contact = ({ language, setLanguage }) => {
  const t = translations[language];
  const [showCalendly, setShowCalendly] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    telephone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    setMapReady(true);
  }, []);

  const contactInfo = [
    {
      id: 1,
      title: t.contact,
      details: "+212 651-913292",
      icon: <FaPhone className="text-xl" />,
    },
    {
      id: 2,
      title: "Email",
      details: "contact@PalaisTouristique.ma",
      icon: <FaEnvelope className="text-xl" />,
    },
    {
      id: 3,
      title: t.address || "Adresse",
      details: "Place Atlas, 7 Rue Hassan Dkhissi, Fès 30000",
      icon: <FaMapMarkerAlt className="text-xl" />,
    },
    {
      id: 4,
      title: t.openingHours || "Heures d'ouverture",
      details:
        t.openingHoursDetails || "Lundi - Vendredi: 8h00 - 18h00",
      icon: <FaClock className="text-xl" />,
    },
  ];

  /*
  const offices = [
    {
      id: 1,
      city: "Fès",
      address: "Place Atlas, 7 Rue Hassan Dkhissi, Fès 30000",
      phone: "+212 651-913292",
    },
    {
      id: 2,
      city: "Rabat",
      address:
        "Boulevard Mohammed VI, Résidence Andalucia, 5ème étage",
      phone: "+212 539 321 654",
    },
    {
      id: 3,
      city: "Casablanca",
      address: "131 Bd D’anfa, Résidence Azur, Bureau n°11B",
      phone: "+212 528 789 123",
    },
  ];
  */

  const faqItems = [
    {
      id: 1,
      question: t.faqQ1 || "Comment réserver un séjour ?",
      answer:
        t.faqA1 ||
        "Vous pouvez réserver directement sur notre site ou nous contacter via le formulaire.",
    },
    {
      id: 2,
      question: t.faqQ2 || "Quels moyens de paiement acceptez-vous ?",
      answer:
        t.faqA2 ||
        "Nous acceptons les paiements par carte bancaire, virement et espèces en agence.",
    },
    {
      id: 3,
      question: t.faqQ3 || "Proposez-vous des offres pour les groupes ?",
      answer:
        t.faqA3 ||
        "Oui, nous avons des offres spéciales pour les groupes et les agences.",
    },
    {
      id: 4,
      question: t.faqQ4 || "Puis-je annuler ou modifier ma réservation ?",
      answer:
        t.faqA4 ||
        "Oui, selon les conditions de l’offre. Contactez-nous pour plus de détails.",
    },
  ];

  const [mapCenter] = useState([34.03285881610196, -4.993798364621835]);
  const [mapZoom] = useState(15);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const data = await contactApi.submit(formData);

      toast.success(data?.message || "Message envoyé ✅");

      setFormData({
        fullName: "",
        email: "",
        subject: "",
        telephone: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      notifyApiError(err, "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="py-10 relative overflow-hidden"
      style={{
        background: "linear-gradient(to bottom, #fff7ed, #f9e9d2 80%)",
      }}
    >
      {/* ✅ Pattern zellij en arrière-plan (comme Services) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.08,
        }}
      />

      {/* ✅ Tu gardes ton radial light par-dessus pour l'effet */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]" />

      <div
        className="container mx-auto px-6 max-w-7xl relative z-10"
        style={{ marginTop: "1.5rem" }}
      >
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
            {t.contactUs || "Contactez-nous"}
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-amber-900">
            <span
              dangerouslySetInnerHTML={{
                __html:
                  t.contactHeroTitle ||
                  "Nous sommes à votre <span class='text-orange-600'>écoute</span>",
              }}
            />
          </h1>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed text-amber-900/80">
            {t.contactHeroDesc ||
              "Une question sur nos services ou une demande personnalisée ? Notre équipe PalaisTouristique est à votre disposition pour vous accompagner dans votre projet."}
          </p>

          <div className="mt-8 inline-flex items-center gap-2 px-5 py-2 rounded-full border border-orange-300/70 bg-white/80 text-orange-700 text-sm font-semibold">
            <FaClock className="text-base" />
            {t.contactAvgResponse15m || "Reponse moyenne en moins de 15 minutes"}
          </div>
        </motion.div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <a
            href="tel:+212651913292"
            className="rounded-2xl bg-white/90 border border-amber-100 shadow-sm p-5 hover:shadow-md transition-shadow"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700/80">{t.contactHotlineTitle || "Hotline directe"}</div>
            <div className="mt-2 text-lg font-bold text-amber-900">+212 651-913292</div>
            <p className="text-sm text-amber-900/80 mt-1">{t.contactHotlineDesc || "Appelez maintenant pour une assistance immediate."}</p>
          </a>

          <button
            type="button"
            onClick={() => setShowCalendly(true)}
            className="text-left rounded-2xl bg-white/90 border border-amber-100 shadow-sm p-5 hover:shadow-md transition-shadow"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700/80">{t.contactAppointmentTitle || "Rendez-vous"}</div>
            <div className="mt-2 text-lg font-bold text-amber-900">{t.contactScheduleCall || "Planifier un appel"}</div>
            <p className="text-sm text-amber-900/80 mt-1">{t.contactAppointmentDesc || "Choisissez un créneau avec notre equipe voyage."}</p>
          </button>

          <a
            href="mailto:contact@PalaisTouristique.ma"
            className="rounded-2xl bg-white/90 border border-amber-100 shadow-sm p-5 hover:shadow-md transition-shadow"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700/80">{t.contactPriorityEmailTitle || "Email prioritaire"}</div>
            <div className="mt-2 text-lg font-bold text-amber-900">contact@PalaisTouristique.ma</div>
            <p className="text-sm text-amber-900/80 mt-1">{t.contactPriorityEmailDesc || "Envoyez votre brief et recevez une proposition detaillee."}</p>
          </a>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white/90 border border-amber-100 rounded-3xl p-6 shadow-xl h-fit"
          >
            <h2 className="text-2xl font-semibold mb-6 text-orange-600">
              {t.sendMessage || "Envoyez-nous un message"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block mb-2 font-medium text-amber-900"
                >
                  {t.fullName || "Nom complet"}
                </label>
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  placeholder={t.fullNamePlaceholder || "Votre nom"}
                  required
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block mb-2 font-medium text-amber-900"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    placeholder={t.emailPlaceholder || "Votre email"}
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block mb-2 font-medium text-amber-900"
                  >
                    {t.telephone || "Téléphone"}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    placeholder={t.telephonePlaceholder || "Votre numéro"}
                    value={formData.telephone}
                    onChange={(e) =>
                      setFormData({ ...formData, telephone: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block mb-2 font-medium text-amber-900"
                >
                  {t.subject || "Sujet"}
                </label>
                <select
                  id="subject"
                  className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900 appearance-none"
                  required
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                >
                  <option value="">
                    {t.selectSubject || "Sélectionnez un sujet"}
                  </option>
                  <option value="reservation">
                    {t.reservation || "Réservation"}
                  </option>
                  <option value="services">
                    {t.customServices || "Services personnalisés"}
                  </option>
                  <option value="partenariat">
                    {t.partnership || "Partenariat"}
                  </option>
                  <option value="autre">{t.other || "Autre"}</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block mb-2 font-medium text-amber-900"
                >
                  {t.message || "Message"}
                </label>
                <textarea
                  id="message"
                  rows={5}
                  className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  placeholder={t.messagePlaceholder || "Votre message"}
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                />
              </div>

              <button
                type="submit"
                className="w-full font-medium py-3 px-6 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl bg-orange-600 text-white hover:bg-orange-700"
                disabled={submitting}
              >
                {submitting
                  ? t.sending || "Envoi en cours..."
                  : t.sendMessageButton || "Envoyer le message"}
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-semibold mb-6 text-orange-600">
                {t.ourContactDetails || "Nos coordonnées"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {contactInfo.map((info) => (
                  <div
                    key={info.id}
                    className="bg-white/80 border border-amber-100 p-5 rounded-3xl hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start">
                      <div className="p-3 rounded-xl mr-4 bg-orange-100 text-orange-600">
                        {info.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold mb-1 text-amber-900">
                          {info.title}
                        </h3>
                        <p className="text-amber-900/80 break-words leading-snug">
                          {info.details}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map Section */}
            <div className="h-64 rounded-3xl overflow-hidden relative bg-white/80 border border-amber-100">
              {mapReady ? (
                <LeafletMap center={mapCenter} zoom={mapZoom} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-amber-900/60">
                    {t.loadingMap || "Chargement de la carte..."}
                  </p>
                </div>
              )}
            </div>

            {/* Appointment CTA */}
            <div className="rounded-3xl p-8 bg-gradient-to-r from-orange-50 via-amber-100 to-orange-50 border border-amber-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full transform translate-x-24 -translate-y-24 blur-3xl bg-orange-100/40" />
              <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full transform -translate-x-32 translate-y-32 blur-3xl bg-orange-100/30" />

              <div className="relative z-10">
                <h3 className="font-semibold text-xl mb-3 text-orange-600">
                  {t.bookAppointment || "Prendre rendez-vous"}
                </h3>
                <p className="mb-4 text-amber-900/80">
                  {t.appointmentDesc ||
                    "Nos conseillers sont disponibles pour des rendez-vous personnalisés. Réservez directement votre créneau en ligne ou contactez-nous par téléphone."}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowCalendly(true)}
                    className="inline-flex items-center justify-center font-medium py-2 px-5 rounded-xl transition-all bg-orange-600 text-white hover:bg-orange-700"
                  >
                    <FaCalendarAlt className="mr-2" />{" "}
                    {t.bookOnline || "Réserver en ligne"}
                  </button>

                  <a
                    href="tel:+212651913292"
                    className="inline-flex items-center justify-center font-medium py-2 px-5 rounded-xl transition-all border border-orange-200 text-orange-600 hover:bg-orange-50"
                  >
                    <FaPhone className="mr-2" />{" "}
                    {t.callNow || "Appeler maintenant"}
                  </a>
                </div>

                {showCalendly && (
                  <div className="mt-6">
                    <div className="bg-white/80 p-4 rounded-xl border border-amber-100">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-orange-600">
                          {t.bookYourAppointment || "Réservez votre rendez-vous"}
                        </h4>
                        <button
                          onClick={() => setShowCalendly(false)}
                          className="text-orange-600 hover:text-orange-800 transition-colors"
                        >
                          <span className="sr-only">{t.close || "Fermer"}</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="w-full h-[400px] flex items-center justify-center text-amber-900/80">
                        <span>
                          {t.calendlyWidget ||
                            "Widget Calendly ici (intégration à faire)"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white/80 p-6 rounded-3xl mb-10 border border-amber-100"
        >
          <h2 className="text-2xl font-semibold mb-8 text-center text-orange-600">
            {t.faqTitle || "Questions fréquentes"}
          </h2>
          <div className="space-y-6">
            {faqItems.map((item) => (
              <div
                key={item.id}
                className="pb-6 last:border-0 last:pb-0 border-b border-amber-100"
              >
                <h3 className="font-semibold text-lg mb-3 text-amber-900">
                  {item.question}
                </h3>
                <p className="text-amber-900/80">{item.answer}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="text-center"
        >
          <div className="rounded-3xl p-12 bg-gradient-to-r from-orange-50 via-amber-100 to-orange-50 border border-amber-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 rounded-full transform translate-x-24 -translate-y-24 blur-3xl bg-orange-100/40" />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full transform -translate-x-32 translate-y-32 blur-3xl bg-orange-100/30" />
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4 text-orange-600">
                {t.readyToPlan || "Prêt à planifier votre séjour ?"}
              </h2>
              <p className="max-w-2xl mx-auto mb-6 text-lg text-amber-900/80">
                {t.readyToPlanDesc ||
                  "Notre équipe PalaisTouristique est prête à vous accompagner pour une expérience inoubliable au Maroc."}
              </p>
              <a
                href="/sejour"
                className="inline-flex items-center px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl bg-orange-600 text-white hover:bg-orange-700"
              >
                {t.discoverOurStays || "Découvrir nos séjours"}{" "}
                <FaChevronRight className="ml-2 text-sm" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
